import { useEffect, useState } from 'react';

import { getAllPosts } from '../api/ghost/ghost';
import type { GhostPostOrPage } from '../api/ghost/ghost-interfaces';
import mapGhostPost from '../api/ghost/mapGhostPage';
import PostType from '../api/typings/PostType';

type Error = {
    message: string;
};

interface UseRecentArticles {
    limit: number;
    tags?: string[];
    supportedAssets?: string;
    authorSlug?: string;
    order?: string;
    page?: number;
}

const useRecentArticles = ({ limit, tags, supportedAssets, authorSlug, order, page }: UseRecentArticles) => {
    const [error, setError] = useState<Error | null>(null);
    const [articles, setArticles] = useState<PostType[]>([] as PostType[]);
    const [totalArticles, setTotalArticles] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [featured, setFeatured] = useState<PostType | null>(null);
    const [isLoading, setIsLoading] = useState<boolean | null>(null);

    useEffect(() => {
        setIsLoading(true);
        getAllPosts({
            limit,
            ...(tags && { tagSlugs: tags || [] }),
            ...(supportedAssets && { supportedAssetNames: supportedAssets ? supportedAssets.split(',') : [] }),
            ...(authorSlug && { authorSlug }),
            ...(order && { order }),
            ...(page && { page }),
        })
            .then(result => {
                if (!Array.isArray(result)) return;

                result.forEach((post: GhostPostOrPage) => {
                    if (post && post.featured === true) {
                        setFeatured(mapGhostPost(post));
                    }
                });

                setTotalArticles(result.meta?.pagination.total);
                setTotalPages(result.meta?.pagination.pages);
                setArticles(result.map(article => mapGhostPost(article)));
            })
            .catch(e => {
                setError(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [limit, (tags || []).join(','), supportedAssets, authorSlug, order, page]);

    return { articles, error, featured, articleCount: (articles || []).length, isLoading, totalArticles, totalPages };
};

export default useRecentArticles;
